import { createContext, ReactNode, useContext, useEffect } from 'react';
import {
  ResolvedWhitelabelConfig,
  whitelabelConfig,
} from '../config/whitelabel';
import { generateBrandPalette } from '../utils/generateBrandPalette';

const WhitelabelContext =
  createContext<ResolvedWhitelabelConfig>(whitelabelConfig);

/**
 * Provides whitelabel config to the app and applies brand customizations
 * (CSS variables, document title, favicon) on mount.
 */
export function WhitelabelProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Apply custom brand color palette as CSS variables
    if (whitelabelConfig.brandColor) {
      const palette = generateBrandPalette(whitelabelConfig.brandColor);
      const root = document.documentElement;
      Object.entries(palette).forEach(([shade, color]) => {
        root.style.setProperty(`--brand-${shade}`, color);
      });
    }

    // Update document title
    document.title = whitelabelConfig.pageTitle;

    // Update favicon
    if (whitelabelConfig.favicon) {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (link) {
        link.href = whitelabelConfig.favicon;
      }
    }
  }, []);

  return (
    <WhitelabelContext.Provider value={whitelabelConfig}>
      {children}
    </WhitelabelContext.Provider>
  );
}

/**
 * Hook to access the resolved whitelabel config.
 *
 * @example
 * ```tsx
 * const { appName, logoFull, logoIcon, isWhitelabel } = useWhitelabel();
 * ```
 */
export function useWhitelabel(): ResolvedWhitelabelConfig {
  return useContext(WhitelabelContext);
}
